import React from 'react';
import PropTypes from 'prop-types';
import Button from '../button/Button';
import childrenPosition from '../ChildPosition';
import CommonAppSetup from '../../../common/common-app-setup/CommonAppSetup';
import Components from "../../../common/Components";
import joinClassNames from "../../../common/JoinClassNames";

const requiredInitial = (props, propName, componentName) => {
	let retval;
	if (!props.initialChildren && !props.initialLabel) {
		retval = new Error(`One of 'initialChildren' or 'initialLabel' is required by '${componentName}' component.`);
	}
	return retval;
};

const requiredPrompt = (props, propName, componentName) => {
	let retval;
	if (!props.promptChildren && !props.promptLabel) {
		retval = new Error(`One of 'promptChildren' or 'promptLabel' is required by '${componentName}' component.`);
	}
	return retval;
};


const propTypes = {
	// children elements for the button
	// this will render both children and initialChildren || promptChildren
	children: PropTypes.node,

	// children node position, use childrenPosition enum
	childrenPosition: PropTypes.string,

	// additional classNames to put on the component
	className: PropTypes.string,

	// children node that allows you to pass in other elements to the button before it was clicked
	initialChildren: requiredInitial,

	// the label/placeholder to show before it was clicked
	initialLabel: requiredInitial,

	// button was clicked the first time (won't fire if disabled)
	onClick: PropTypes.func,

	// button was clicked twice
	onConfirm: PropTypes.func.isRequired,

	// children node that allows you to pass in other elements to the button after it was clicked
	promptChildren: requiredPrompt,

	// the label/placeholder to show after it was clicked
	promptLabel: requiredPrompt,
};

const defaultProps = {
	children: undefined,
	childrenPosition: childrenPosition.AFTER,
	className: undefined,
	initialChildren: undefined,
	initialLabel: undefined,
	onClick: undefined,
	promptChildren: undefined,
	promptLabel: undefined,
};

export default class ConfirmationButton extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			clicked: false,
			busy: false
		};

		this.clickButton = this.clickButton.bind(this);
		this.confirmToggle = this.confirmToggle.bind(this);
		this.handleCancel = this.handleCancel.bind(this);

		this.timer = undefined;
	}

	componentWillUnmount() {
		this.removeEventHandlers();
	}

	clickButton(onClick, onConfirm) {
		if (!this.state.busy) {
			if (this.state.clicked) {
				onConfirm();
			} else if (onClick) {
				onClick();
			}
			this.setState({ busy: true });
			if (!this.state.clicked) {
				this.timer = setTimeout(this.confirmToggle, 350);
			} else {
				this.confirmToggle();
			}
		}
	}

	confirmToggle() {
		clearTimeout(this.timer);
		this.timer = undefined;
		const newClickedState = !this.state.clicked;
		if (newClickedState) {
			this.addEventHandlers();
		} else {
			this.removeEventHandlers();
		}
		this.setState({ clicked: newClickedState, busy: false });
	}

	setChildren(children, initialChildren, promptChildren) {
		let returnChildren;
		if (children || (!this.state.clicked && initialChildren) || (this.state.clicked && promptChildren)) {
			returnChildren = (
				<React.Fragment>
					{children && children}
					{(!this.state.clicked && initialChildren) ? initialChildren : undefined}
					{(this.state.clicked && promptChildren) ? promptChildren : undefined}
				</React.Fragment>
			);
		}
		return returnChildren;
	}

	addEventHandlers() {
		document.addEventListener('keydown', this.handleCancel);
		document.addEventListener('click', this.handleCancel);
	}

	removeEventHandlers() {
		document.removeEventListener('keydown', this.handleCancel);
		document.removeEventListener('click', this.handleCancel);
	}

	handleCancel(e) {
		if ((e.type === 'keydown' && e.key === "Escape") || (e.type === 'click')) {
			this.setState({clicked: false});
			this.removeEventHandlers();
		}
	}

	render() {
		const {
			children,
			childrenPosition,
			className,
			initialChildren,
			initialLabel,
			//button label shouldn't be used, therefore we need to handle it here. (do nothing with it)
			//eslint-disable-next-line no-unused-vars
			label,
			onClick,
			onConfirm,
			promptChildren,
			promptLabel,
			...passThroughProps
		} = this.props;

		return (
			<Button
				label={this.state.clicked ?  promptLabel : initialLabel}
				children={this.setChildren(children, initialChildren, promptChildren)}
				childrenPosition={childrenPosition}
				onClick={() => this.clickButton(onClick,onConfirm)}
				className={joinClassNames(
					className,
					CommonAppSetup.getClassName(Components.CONFIRMATION_BUTTON, 'button'),
					(this.state.clicked ? 'prompt' : 'initial'),
					(this.state.busy ? 'triggered' : undefined)
				)}
				{...passThroughProps}
			/>
		);
	}
}

ConfirmationButton.childrenPostion = childrenPosition;
ConfirmationButton.propTypes = propTypes;
ConfirmationButton.defaultProps = defaultProps;
